import { Business, FolderOpen, Person } from '@mui/icons-material'
import { Box, Stack, Typography } from '@mui/material'
import { FC, useEffect, useRef } from 'react'
import { hardNavigate } from './utils'
import { getPxAsRem } from 'shared/utils'
import { EntityType } from './types'

interface ResultItemProps {
    type: EntityType
    name: string
    subtitle?: string
    path: string
    onSelect?: () => void
    isHighlighted?: boolean
}

function getIcon(type: EntityType) {
    switch (type) {
        case 'candidate':
            return <Person fontSize="small" />
        case 'roster':
            return <FolderOpen fontSize="small" />
        case 'company':
            return <Business fontSize="small" />
    }
}

export const ResultItem: FC<ResultItemProps> = ({
    type,
    name,
    subtitle,
    path,
    isHighlighted = false,
}) => {
    const itemRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (isHighlighted && itemRef.current) {
            itemRef.current.scrollIntoView({
                block: 'center',
                behavior: 'smooth',
            })
        }
    }, [isHighlighted])

    const handleClick = (e: React.MouseEvent) => {
        if (e.metaKey || e.ctrlKey) {
            window.open(path, '_blank')
        } else {
            hardNavigate(path)
        }
    }

    return (
        <Box
            ref={itemRef}
            onClick={handleClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: getPxAsRem(12),
                padding: getPxAsRem(0, 12),
                height: getPxAsRem(48),
                cursor: 'pointer',
                borderRadius: getPxAsRem(4),
                backgroundColor: isHighlighted
                    ? 'other.background'
                    : 'transparent',
                '&:hover': {
                    backgroundColor: 'other.background',
                },
            }}
        >
            <Box
                sx={{
                    color: 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {getIcon(type)}
            </Box>
            <Stack spacing={0} flex={1} minWidth={0} justifyContent="center">
                <Typography
                    variant="body2"
                    fontWeight={500}
                    noWrap
                    sx={{ lineHeight: 1.3 }}
                >
                    {name}
                </Typography>
                {subtitle && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ lineHeight: 1.3 }}
                    >
                        {subtitle}
                    </Typography>
                )}
            </Stack>
        </Box>
    )
}
